import {
  type Configuration,
  type INavigationClient,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';
import type {
  INetworkModule,
  NetworkRequestOptions,
} from '@azure/msal-common/browser';
// Note: MSW-specific redirect handler has been removed; unit tests no longer rely on it.
import { type EntraUser, loggedInUser } from './EntraUsers';
import { MOCK_REDIRECT_PATH } from './navigationClient';

const DEFAULT_SCOPE = '/.default';
const AUTHORIZATION_CODE_PREFIX = 'mock_code.';
const KNOWN_MOCK_STANDARD_SCOPES = new Set([
  'openid',
  'profile',
  'offline_access',
]);

const defaultTestConfig: Configuration = {
  auth: {
    clientId: 'mock-client-id',
    redirectUri: globalThis.location.origin,
    postLogoutRedirectUri: globalThis.location.origin,
  },
};

const toBase64Url = (value: string): string => {
  return globalThis
    .btoa(value)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const fromBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  const padded =
    padding === 0 ? normalized : `${normalized}${'='.repeat(4 - padding)}`;
  return globalThis.atob(padded);
};

const createJwt = (payload: Record<string, unknown>): string => {
  const header = { alg: 'none', typ: 'JWT' };
  return `${toBase64Url(JSON.stringify(header))}.${toBase64Url(JSON.stringify(payload))}.mock-signature`;
};
const decodeAuthorizationCode = (
  code: string | null,
): { username: string | null; nonce: string | null } => {
  if (code === null || !code.startsWith(AUTHORIZATION_CODE_PREFIX)) {
    return { username: null, nonce: null };
  }

  const payload = JSON.parse(
    fromBase64Url(code.slice(AUTHORIZATION_CODE_PREFIX.length)),
  ) as { username?: string; nonce?: string | null };

  return {
    username: payload.username ?? null,
    nonce: payload.nonce ?? null,
  };
};

const inferResourceFromScopes = (
  scopes: readonly string[],
  defaultClientId: string,
): string => {
  const firstScope = scopes[0];
  if (!firstScope) {
    return `api://${defaultClientId}`;
  }

  const slashIndex = firstScope.lastIndexOf('/');
  if (slashIndex <= 0) {
    return `api://${defaultClientId}`;
  }

  return firstScope.slice(0, slashIndex);
};

const scopePermission = (scope: string): string => {
  const slashIndex = scope.lastIndexOf('/');
  return slashIndex === -1 ? scope : scope.slice(slashIndex + 1);
};

const normalizeScopes = (
  requestedScopes: readonly string[],
  availableScopes: readonly string[],
): string[] => {
  const resolved: string[] = [];

  for (const scope of requestedScopes) {
    if (KNOWN_MOCK_STANDARD_SCOPES.has(scope)) {
      continue;
    }

    if (scope.endsWith(DEFAULT_SCOPE)) {
      const resource = scope.slice(0, -DEFAULT_SCOPE.length);
      const resourceMatches: string[] = [];
      for (const allowedScope of availableScopes) {
        if (allowedScope.startsWith(`${resource}/`)) {
          resourceMatches.push(allowedScope);
        }
      }

      if (resourceMatches.length > 0) {
        resolved.push(...resourceMatches);
        continue;
      }

      resolved.push(
        ...availableScopes.map(
          (availableScope) => `${resource}/${scopePermission(availableScope)}`,
        ),
      );
      continue;
    }

    const permissionMatches = availableScopes.filter(
      (allowedScope) => scopePermission(allowedScope) === scope,
    );
    if (permissionMatches.length > 0) {
      resolved.push(...permissionMatches);
      continue;
    }

    if (availableScopes.includes(scope)) {
      resolved.push(scope);
    }
  }

  return [...new Set(resolved)];
};

const buildTokenResponseForUser = (
  user: EntraUser,
  scopes: readonly string[],
  clientId: string,
  authority: string,
  nonce: string | null,
): {
  token_type: 'Bearer';
  scope: string;
  expires_in: number;
  ext_expires_in: number;
  access_token: string;
  refresh_token: string;
  id_token: string;
  client_info: string;
} => {
  const now = Math.floor(Date.now() / 1000);
  const aud = inferResourceFromScopes(scopes, clientId);
  const accessToken = createJwt({
    aud,
    iss: authority,
    tid: user.tenantId,
    oid: user.localAccountId,
    sub: user.localAccountId,
    preferred_username: user.username,
    scp: scopes.map(scopePermission).join(' '),
    iat: now,
    exp: now + 3600,
  });
  const idToken = createJwt({
    aud: clientId,
    iss: authority,
    tid: user.tenantId,
    oid: user.localAccountId,
    sub: user.localAccountId,
    preferred_username: user.username,
    name: user.name,
    nonce,
    iat: now,
    exp: now + 3600,
  });
  const clientInfo = toBase64Url(
    JSON.stringify({
      uid: user.localAccountId,
      utid: user.tenantId,
    }),
  );

  return {
    token_type: 'Bearer',
    scope: scopes.join(' '),
    expires_in: 3600,
    ext_expires_in: 3600,
    access_token: accessToken,
    refresh_token: `mock_refresh_token.${user.localAccountId}`,
    id_token: idToken,
    client_info: clientInfo,
  };
};

class MockMsalNetworkClient implements INetworkModule {
  private readonly usersByUsername: Map<string, EntraUser>;

  constructor(
    users: readonly EntraUser[],
    private readonly fallbackUser: EntraUser | null,
    private readonly clientId: string,
    private readonly authority: string,
  ) {
    this.usersByUsername = new Map(
      users.map((user) => [user.username.toLowerCase(), user]),
    );
  }

  sendGetRequestAsync<T>(
    _url: string,
    _options?: NetworkRequestOptions,
    _timeout?: number,
  ): Promise<{ headers: Record<string, string>; body: T; status: number }> {
    return Promise.resolve({
      headers: {},
      body: {} as T,
      status: 200,
    });
  }

  sendPostRequestAsync<T>(
    _url: string,
    options?: NetworkRequestOptions,
  ): Promise<{ headers: Record<string, string>; body: T; status: number }> {
    const params = new URLSearchParams(options?.body ?? '');
    const requestScopes = (params.get('scope') ?? '')
      .split(' ')
      .map((scope) => scope.trim())
      .filter((scope) => scope.length > 0);
    const code = params.get('code');
    const { username: usernameFromCode, nonce } = decodeAuthorizationCode(code);
    const username =
      usernameFromCode?.toLowerCase() ??
      this.fallbackUser?.username.toLowerCase() ??
      null;
    const user =
      username === null ? null : (this.usersByUsername.get(username) ?? null);

    if (user === null) {
      return Promise.resolve({
        headers: {},
        status: 400,
        body: {
          error: 'invalid_grant',
          error_description: 'MockMsal: unknown user',
        } as T,
      });
    }

    const scopes = normalizeScopes(requestScopes, user.availableJwtScopes);
    if (scopes.length === 0) {
      return Promise.resolve({
        headers: {},
        status: 400,
        body: {
          error: 'invalid_scope',
          error_description: 'MockMsal: requested scopes are not available',
        } as T,
      });
    }

    const tokenResponse = buildTokenResponseForUser(
      user,
      scopes,
      this.clientId,
      this.authority,
      nonce,
    );

    return Promise.resolve({
      headers: { 'content-type': 'application/json' },
      status: 200,
      body: tokenResponse as T,
    });
  }
}

const buildMockMsalConfiguration = (
  config: Configuration,
  users: readonly EntraUser[],
  activeUser: EntraUser | null,
): Configuration => {
  const origin = globalThis.location.origin;
  // MSAL requires an HTTPS authority, but the authority *path* influences the
  // generated authorize URL. Use a stable path so we can keep a single
  // redirect landing path.
  const mockAuthority = 'https://mock.login.microsoftonline.com/mock-auth';
  const metadata = {
    issuer: mockAuthority,
    authorization_endpoint: new URL(MOCK_REDIRECT_PATH, origin).toString(),
    token_endpoint: new URL('/mock-auth/token', origin).toString(),
    jwks_uri: new URL('/mock-auth/keys', origin).toString(),
    response_types_supported: ['code'],
    response_modes_supported: ['fragment', 'query'],
    subject_types_supported: ['pairwise'],
    id_token_signing_alg_values_supported: ['RS256'],
    token_endpoint_auth_methods_supported: ['none'],
    scopes_supported: ['openid', 'profile', 'offline_access'],
    claims_supported: [
      'aud',
      'iss',
      'sub',
      'exp',
      'iat',
      'name',
      'preferred_username',
      'oid',
      'tid',
    ],
  };

  const networkClient = new MockMsalNetworkClient(
    users,
    activeUser,
    config.auth.clientId,
    mockAuthority,
  );

  return {
    ...config,
    auth: {
      ...config.auth,
      authority: mockAuthority,
      redirectUri: config.auth.redirectUri ?? origin,
      postLogoutRedirectUri: config.auth.postLogoutRedirectUri ?? origin,
      authorityMetadata: JSON.stringify(metadata),
      knownAuthorities: [
        ...(config.auth.knownAuthorities ?? []),
        new URL(mockAuthority).host,
      ],
    },
    system: {
      ...config.system,
      networkClient,
    },
  };
};

export class MockPublicClientApplication extends PublicClientApplication {
  public mode = 'mock' as const;

  private readonly preferredUsername: string | null;

  constructor(
    user: EntraUser | null = loggedInUser,
    navigationClient: INavigationClient | null = null,
    users?: EntraUser[],
    config: Configuration = defaultTestConfig,
  ) {
    const resolvedUsers = users ?? (user === null ? [] : [user]);
    const mergedConfig = buildMockMsalConfiguration(
      config,
      resolvedUsers,
      user,
    );

    super(mergedConfig);

    this.preferredUsername = user?.username ?? null;
    if (navigationClient !== null) {
      this.setNavigationClient(navigationClient);
    }
  }

  private withRedirectOutcome(request: RedirectRequest): RedirectRequest {
    const loginHint =
      request.loginHint ??
      this.getActiveAccount()?.username ??
      this.preferredUsername;
    return {
      ...request,
      extraQueryParameters: {
        mock_outcome: 'success',
        ...(loginHint === null ? {} : { login_hint: loginHint }),
        ...(request.extraQueryParameters ?? {}),
      },
    };
  }

  override acquireTokenRedirect(request: RedirectRequest): Promise<void> {
    return super.acquireTokenRedirect(this.withRedirectOutcome(request));
  }

  override loginRedirect(request?: RedirectRequest): Promise<void> {
    if (request === undefined) {
      return super.loginRedirect();
    }
    return super.loginRedirect(this.withRedirectOutcome(request));
  }
}

// Re-export navigation clients so consumers have one import location
export {
  MockNavigationClient,
  UnitTestNavigationClient,
} from './navigationClient';
